import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';

const PageContainer = styled.div`
  min-height: 100vh;
  background: ${(props) => props.theme.colors.background.dark};
  color: ${(props) => props.theme.colors.text.primary};
  font-family: ${(props) => props.theme.fonts.primary};
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 120px 60px 120px;

  @media (max-width: 768px) {
    padding: 120px 24px 120px;
  }
`;

const AnimatedSection = styled.section`
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  transform: translateY(${(props) => (props.isVisible ? '0' : '30px')});
  transition: opacity 0.7s ease-out, transform 0.7s ease-out;
  margin-bottom: 0;
`;

const Title = styled.h1`
  font-size: 56px;
  font-weight: 700;
  margin-bottom: 24px;
  line-height: 1.1;
  color: ${(props) => props.theme.colors.text.primary};
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const Subtitle = styled.p`
  font-size: 20px;
  font-weight: 400;
  color: ${(props) => props.theme.colors.text.secondary};
  line-height: 1.6;
  margin: 0;
  max-width: 800px;
`;

const SectionWrapper = styled.div`
  background: ${(props) => props.background || 'transparent'};
  padding: ${(props) => props.padding || '80px 0'};
  margin-left: -60px;
  margin-right: -60px;
  padding-left: 60px;
  padding-right: 60px;

  @media (max-width: 768px) {
    margin-left: -24px;
    margin-right: -24px;
    padding-left: 24px;
    padding-right: 24px;
    padding-top: 60px;
    padding-bottom: 60px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 42px;
  font-weight: 700;
  color: ${(props) => props.theme.colors.text.primary};
  margin-top: 48px;
  margin-bottom: 16px;
  line-height: 1.2;
  letter-spacing: -0.01em;

  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const Paragraph = styled.p`
  font-size: 18px;
  line-height: 1.7;
  color: ${(props) => props.theme.colors.text.secondary};
  margin-bottom: 20px;
  max-width: 800px;

  &:last-child {
    margin-bottom: 0;
  }

  strong {
    color: ${(props) => props.theme.colors.text.primary};
    font-weight: 600;
  }
`;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  margin: 48px 0;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const Column = styled.div``;

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 24px 0;
`;

const ListItem = styled.li`
  font-size: 17px;
  line-height: 1.7;
  color: ${(props) => props.theme.colors.text.secondary};
  margin-bottom: 16px;
  padding-left: 28px;
  position: relative;

  &:before {
    content: '—';
    position: absolute;
    left: 0;
    color: ${(props) => props.theme.colors.primary};
    font-weight: 600;
  }

  &:last-child {
    margin-bottom: 0;
  }

  strong {
    color: ${(props) => props.theme.colors.text.primary};
    font-weight: 600;
  }
`;

const SectionSubtitle = styled.h3`
  font-size: 24px;
  font-weight: 600;
  color: ${(props) => props.theme.colors.text.primary};
  margin: 0 0 20px 0;
  line-height: 1.3;
`;

/* Pricing Comparison Styles */

const PricingCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 48px 40px;
  position: relative;
  backdrop-filter: blur(10px);
  overflow: hidden;
`;

const CardHeader = styled.div`
  text-align: center;
  margin-bottom: 32px;
`;

const IconCircle = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  margin: 0 auto 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) =>
    props.error ? 'rgba(239, 68, 68, 0.15)' : 'rgba(34, 197, 94, 0.15)'};

  svg {
    width: 32px;
    height: 32px;
  }
`;

const CardTitle = styled.div`
  color: #ffffff;
  font-size: 24px;
  font-weight: 700;
  letter-spacing: 0.05em;
`;

const TicketStub = styled.div`
  background: rgba(0, 0, 0, 0.4);
  border-radius: 12px;
  position: relative;
  margin-bottom: 24px;
  overflow: visible;
  display: flex;
  min-height: 140px;
  box-sizing: border-box;
  width: 100%;
`;

const TicketContent = styled.div`
  flex: 0 0 85%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 24px;
  position: relative;
  box-sizing: border-box;

  &::after {
    content: '';
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: 2px;
    background: repeating-linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0.1) 0px,
      rgba(255, 255, 255, 0.1) 4px,
      transparent 4px,
      transparent 8px
    );
  }
`;

const TicketStubSection = styled.div`
  flex: 0 0 15%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 0 12px 12px 0;
  isolation: isolate;
  box-sizing: border-box;
`;

const PriceSection = styled.div`
  text-align: center;
`;

const PriceMain = styled.div`
  font-size: 56px;
  font-weight: 700;
  color: #ffffff;
  line-height: 1;
  margin-bottom: 8px;

  @media (max-width: 768px) {
    font-size: 42px;
  }
`;

const PriceLabel = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: #22c55e;
  letter-spacing: 0.05em;
`;

const SideLabel = styled.div`
  writing-mode: vertical-rl;
  text-orientation: mixed;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.2em;
  color: rgba(255, 255, 255, 0.4);
  text-transform: uppercase;
`;

const FeesList = styled.ul`
  list-style: none;
  margin-bottom: 20px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 12px;
  padding: 20px;
`;

const FeeItem = styled.li`
  display: flex;
  justify-content: space-between;
  color: rgba(255, 255, 255, 0.7);
  font-size: 15px;
  margin-bottom: 10px;
  padding: 8px 0;

  &:last-child {
    margin-bottom: 0;
  }
`;

const FeeName = styled.span`
  color: rgba(255, 255, 255, 0.6);
`;

const FeeAmount = styled.span`
  color: rgba(255, 255, 255, 0.85);
  font-weight: 600;
`;

const TotalLine = styled.div`
  color: #ffffff;
  font-weight: 600;
  text-align: center;
  font-size: 16px;
  margin-top: 16px;
  padding: 16px;
  background: rgba(239, 68, 68, 0.1);
  border-radius: 8px;
  border: 1px solid rgba(239, 68, 68, 0.2);
`;

const SimpleDisplay = styled.div`
  text-align: center;
  padding: 20px;

  p {
    color: rgba(255, 255, 255, 0.7);
    font-size: 16px;
    line-height: 1.6;
    margin: 0;
  }
`;

/* ANIMATION STYLES */
const TwoColumnLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: start;
  margin: 0;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const TextColumn = styled.div``;

const CardsColumn = styled.div`
  position: relative;
  min-height: 600px;
`;

/* Animated Card with Scroll-Jacking */
const AnimatedCard = styled(PricingCard)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  transition: opacity 0.2s ease-out, transform 0.2s ease-out;

  ${(props) =>
    props.isOld &&
    `
    opacity: ${1 - props.progress};
    transform: rotateZ(${-45 * props.progress}deg) scale(${
      1 - 0.2 * props.progress
    });
    pointer-events: ${props.progress > 0.5 ? 'none' : 'auto'};
    z-index: ${props.progress > 0.5 ? 1 : 2};
  `}

  ${(props) =>
    props.isNew &&
    `
    opacity: ${props.progress};
    transform: rotateZ(${45 - 45 * props.progress}deg) scale(${
      0.8 + 0.2 * props.progress
    });
    pointer-events: ${props.progress < 0.5 ? 'none' : 'auto'};
    z-index: ${props.progress > 0.5 ? 2 : 1};
  `}
`;

/* Social Post Styles */
const PostsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const PostCard = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark' ? '#2d3748' : '#ffffff'};
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);

  /* Initial state */
  opacity: 0;
  transform: translateX(50px);
  transition: opacity 0.6s ease-out, transform 0.6s ease-out,
    box-shadow 0.2s ease;

  /* Animate when parent is visible */
  ${(props) =>
    props.isVisible &&
    `
    opacity: 1;
    transform: translateX(0);
  `}

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }
`;

const PostHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
`;

const PostAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #fa4616 0%, #ff8c42 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 16px;
  color: #fff;
  flex-shrink: 0;
`;

const PostAuthorInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const PostAuthorName = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: ${(props) => props.theme.colors.text.primary};
`;

const PostMeta = styled.div`
  font-size: 12px;
  color: ${(props) => props.theme.colors.text.secondary};
`;

const PostContent = styled.div`
  font-size: 14px;
  line-height: 1.5;
  color: ${(props) => props.theme.colors.text.secondary};
  margin-bottom: 12px;

  strong {
    color: ${(props) => props.theme.colors.text.primary};
    font-weight: 600;
  }
`;

const PostStats = styled.div`
  display: flex;
  gap: 16px;
  font-size: 12px;
  color: ${(props) => props.theme.colors.text.secondary};
  padding-top: 12px;
  border-top: 1px solid ${(props) => props.theme.colors.border};
`;

const PostStat = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
`;

const ArticleCard = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark' ? '#2d3748' : '#ffffff'};
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);

  /* Initial state */
  opacity: 0;
  transform: translateX(50px);
  transition: opacity 0.6s ease-out 1s, transform 0.6s ease-out 1s,
    box-shadow 0.2s ease;

  /* Animate when parent is visible with delay */
  ${(props) =>
    props.isVisible &&
    `
    opacity: 1;
    transform: translateX(0);
  `}

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }
`;

const ArticleImageWrapper = styled.div`
  position: relative;
`;

const ArticleImage = styled.img`
  width: 100%;
  height: 140px;
  object-fit: cover;
`;

const ArticleBadge = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  background: rgba(255, 255, 255, 0.95);
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #1a1a1a;
  backdrop-filter: blur(8px);
`;

const ArticleContent = styled.div`
  padding: 14px 16px;
`;

const ArticleSource = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${(props) => props.theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
`;

const ArticleHeadline = styled.div`
  font-size: 14px;
  font-weight: 700;
  line-height: 1.4;
  color: ${(props) => props.theme.colors.text.primary};
  margin-bottom: 6px;
`;

const ArticleDate = styled.div`
  font-size: 11px;
  color: ${(props) => props.theme.colors.text.secondary};
`;

const Footer = styled.footer`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(26, 26, 46, 0.95)'
      : 'rgba(255, 255, 255, 0.95)'};
  backdrop-filter: blur(12px);
  border-top: 1px solid ${(props) => props.theme.colors.border};
  padding: 24px 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 100;

  @media (max-width: 768px) {
    padding: 20px 24px;
    flex-direction: column;
    gap: 16px;
  }
`;

const Progress = styled.div`
  color: ${(props) => props.theme.colors.text.secondary};
  font-size: 14px;
  font-weight: 500;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
`;

const Button = styled.button`
  background: ${(props) =>
    props.primary ? props.theme.colors.primary : 'transparent'};
  border: ${(props) =>
    props.primary ? 'none' : `1px solid ${props.theme.colors.border}`};
  color: ${(props) =>
    props.primary ? '#ffffff' : props.theme.colors.text.primary};
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  font-family: ${(props) => props.theme.fonts.primary};

  &:hover {
    transform: translateY(-1px);
    background: ${(props) =>
      props.primary
        ? '#E03E0C'
        : props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.05)'};
  }
`;

const AllInPricingComing = () => {
  const history = useHistory();
  const location = useLocation();
  const [visibleSections, setVisibleSections] = useState({});
  const [animationProgress, setAnimationProgress] = useState(0);
  const sectionRefs = useRef({});
  const animationRef = useRef(null);
  const scrollAccumulator = useRef(0);
  const scrollLocked = useRef(false);
  const lockedScrollPosition = useRef(0);
  const animationFrameId = useRef(null);

  // Determine route prefix based on current path
  const { pathname } = location;
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => ({
              ...prev,
              [entry.target.dataset.section]: true,
            }));
          }
        });
      },
      { threshold: 0.15 }
    );

    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  // Improved scroll-jacking with better fast scroll handling
  useEffect(() => {
    const SCROLL_REQUIREMENT = 600; // Increased for smoother, more controlled animation
    const BUFFER_ZONE = 15; // Slightly larger buffer

    const updateProgress = () => {
      const progress = Math.max(
        0,
        Math.min(1, scrollAccumulator.current / SCROLL_REQUIREMENT)
      );
      setAnimationProgress(progress);
    };

    const handleWheel = (e) => {
      const element = animationRef.current;
      if (!element) return;

      const rect = element.getBoundingClientRect();
      // Adjusted for new padding - wider engagement zone
      const inStablePosition = rect.top < 300 && rect.top > -50;

      if (!inStablePosition) {
        // Not in animation zone, allow normal scroll
        scrollLocked.current = false;
        return;
      }

      const scrollingDown = e.deltaY > 0;
      const currentProgress = scrollAccumulator.current / SCROLL_REQUIREMENT;

      // Check if we should hijack based on boundaries with buffer
      const atStart = currentProgress <= BUFFER_ZONE / SCROLL_REQUIREMENT;
      const atEnd = currentProgress >= 1 - BUFFER_ZONE / SCROLL_REQUIREMENT;

      if ((atStart && !scrollingDown) || (atEnd && scrollingDown)) {
        // At boundary scrolling away - allow escape
        scrollLocked.current = false;
        return;
      }

      // Hijack the scroll
      e.preventDefault();

      // Smoother delta capping (max 30px per wheel event for more control)
      const cappedDelta = Math.max(-30, Math.min(30, e.deltaY));

      // Update accumulator bidirectionally
      if (scrollingDown) {
        scrollAccumulator.current = Math.min(
          SCROLL_REQUIREMENT,
          scrollAccumulator.current + Math.abs(cappedDelta)
        );
      } else {
        scrollAccumulator.current = Math.max(
          0,
          scrollAccumulator.current - Math.abs(cappedDelta)
        );
      }

      // Use requestAnimationFrame for smooth updates
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
      animationFrameId.current = requestAnimationFrame(updateProgress);

      // Lock scroll position
      if (!scrollLocked.current) {
        scrollLocked.current = true;
        lockedScrollPosition.current = window.scrollY;
      }
      window.scrollTo(0, lockedScrollPosition.current);
    };

    window.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      window.removeEventListener('wheel', handleWheel);
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, []);

  const handleNext = () => {
    history.push(`${routePrefix}/pricing-educator/understand-all-in-pricing`);
  };

  const handleSkipToCalculator = () => {
    history.push(`${routePrefix}/pricing-educator/calculator`);
  };

  // Reusable card content
  const renderOldPricingCard = () => (
    <>
      <CardHeader>
        <IconCircle error>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="#EF4444"
            strokeWidth="2.5"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
          </svg>
        </IconCircle>
        <CardTitle>OLD PRICING</CardTitle>
      </CardHeader>

      <TicketStub>
        <TicketContent>
          <PriceSection>
            <PriceMain>$25.00</PriceMain>
          </PriceSection>
        </TicketContent>
        <TicketStubSection>
          <SideLabel>FEES</SideLabel>
        </TicketStubSection>
      </TicketStub>

      <FeesList>
        <FeeItem>
          <FeeName>Ticket Fee</FeeName>
          <FeeAmount>$1.25</FeeAmount>
        </FeeItem>
        <FeeItem>
          <FeeName>Order Processing Fee</FeeName>
          <FeeAmount>$0.90</FeeAmount>
        </FeeItem>
      </FeesList>

      <TotalLine>Total: $27.15 (at checkout)</TotalLine>
    </>
  );

  const renderNewPricingCard = () => (
    <>
      <CardHeader>
        <IconCircle>
          <svg viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="3">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </IconCircle>
        <CardTitle>NEW PRICING</CardTitle>
      </CardHeader>

      <TicketStub>
        <TicketContent>
          <PriceSection>
            <PriceMain>$28.00</PriceMain>
            <PriceLabel>ALL-IN</PriceLabel>
          </PriceSection>
        </TicketContent>
        <TicketStubSection>
          <SideLabel>ALL-IN</SideLabel>
        </TicketStubSection>
      </TicketStub>

      <SimpleDisplay>
        <p>One clear price. No surprises. No confusion at checkout.</p>
      </SimpleDisplay>
    </>
  );

  return (
    <EnhancedThemeProvider>
      <PageContainer>
        <ContentWrapper>
          {/* Section 1: What Is All-In Pricing */}
          <AnimatedSection
            ref={(el) => {
              sectionRefs.current['what'] = el;
              animationRef.current = el;
            }}
            data-section="what"
            isVisible={visibleSections['what']}
          >
            <TwoColumnLayout>
              <TextColumn>
                <Title>All-In Pricing Is Coming</Title>
                <Subtitle>
                  New laws and ticketing trends are changing how prices are
                  shown to fans. Learn what's driving the shift, what's
                  required, and how to stay ahead in 2026.
                </Subtitle>
                <SectionTitle>What Is All-In Pricing?</SectionTitle>
                <Paragraph>
                  All-in pricing means the price fans see first is the total
                  price they'll pay — every required fee included (except sales
                  tax).
                </Paragraph>
                <Paragraph
                  style={{
                    marginTop: '32px',
                    fontSize: '20px',
                    fontWeight: 600,
                    color: '#ffffff',
                  }}
                >
                  The goal: no surprises, no confusion, and more trust between
                  fans and venues.
                </Paragraph>
              </TextColumn>

              <CardsColumn>
                <AnimatedCard isOld progress={animationProgress}>
                  {renderOldPricingCard()}
                </AnimatedCard>
                <AnimatedCard isNew progress={animationProgress}>
                  {renderNewPricingCard()}
                </AnimatedCard>
              </CardsColumn>
            </TwoColumnLayout>
          </AnimatedSection>
        </ContentWrapper>

        {/* Section 2: Legislative Landscape - Gray Background */}
        <SectionWrapper background="#1a1a2e" padding="60px 0">
          {/* theme.colors.background.secondary */}
          <div
            style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 60px' }}
          >
            <AnimatedSection
              ref={(el) => (sectionRefs.current['legislative'] = el)}
              data-section="legislative"
              isVisible={visibleSections['legislative']}
            >
              <SectionTitle>
                The Legislative Landscape & Industry Shift
              </SectionTitle>

              <TwoColumnGrid>
                <Column>
                  <Paragraph style={{ marginBottom: '32px' }}>
                    High-profile controversies around hidden fees sparked
                    consumer outrage and political pressure for reform.
                    Lawmakers responded with new transparency requirements.
                  </Paragraph>
                  <Paragraph style={{ marginBottom: '32px' }}>
                    Several states have already passed all-in pricing laws,
                    starting with California in 2024, and the federal TICKETS
                    Act has bipartisan support with a nationwide rollout likely
                    by mid-2026.
                  </Paragraph>
                  <Paragraph style={{ marginBottom: '32px' }}>
                    At the same time, major ticketing platforms and promoters
                    are proactively adopting transparent pricing to restore fan
                    trust and stay ahead of regulation.
                  </Paragraph>
                </Column>

                <Column>
                  <PostsStack>
                    <PostCard isVisible={visibleSections['legislative']}>
                      <PostHeader>
                        <PostAvatar>JM</PostAvatar>
                        <PostAuthorInfo>
                          <PostAuthorName>Concert Goer</PostAuthorName>
                          <PostMeta>@musicfan · Dec 2, 2022</PostMeta>
                        </PostAuthorInfo>
                      </PostHeader>
                      <PostContent>
                        Saw <strong>$49 tickets</strong>. Got to checkout:{' '}
                        <strong>$87</strong>. How is a $38 "service fee" even
                        legal? This is why people hate buying tickets online.
                      </PostContent>
                      <PostStats>
                        <PostStat>
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                          </svg>
                          <span>2.1K</span>
                        </PostStat>
                        <PostStat>
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="17 1 21 5 17 9"></polyline>
                            <path d="M3 11V9a4 4 0 0 1 4-4h14"></path>
                            <polyline points="7 23 3 19 7 15"></polyline>
                            <path d="M21 13v2a4 4 0 0 1-4 4H3"></path>
                          </svg>
                          <span>8.4K</span>
                        </PostStat>
                        <PostStat>
                          <svg
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            stroke="none"
                          >
                            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                          </svg>
                          <span>24K</span>
                        </PostStat>
                      </PostStats>
                    </PostCard>

                    <ArticleCard isVisible={visibleSections['legislative']}>
                      <ArticleImageWrapper>
                        <ArticleImage
                          src="/images/taylor-swift-concert.jpg"
                          alt="Taylor Swift Concert"
                        />
                        <ArticleBadge>Breaking</ArticleBadge>
                      </ArticleImageWrapper>
                      <ArticleContent>
                        <ArticleSource>The New York Times</ArticleSource>
                        <ArticleHeadline>
                          "The Fees Are Too Damn High": How Ticketmaster Became
                          America's Most Hated Company
                        </ArticleHeadline>
                        <ArticleDate>December 3, 2022</ArticleDate>
                      </ArticleContent>
                    </ArticleCard>
                  </PostsStack>
                </Column>
              </TwoColumnGrid>
            </AnimatedSection>
          </div>
        </SectionWrapper>

        {/* Section 3: What These Laws Require - Two Column Layout */}
        <ContentWrapper>
          <AnimatedSection
            ref={(el) => (sectionRefs.current['laws'] = el)}
            data-section="laws"
            isVisible={visibleSections['laws']}
          >
            <SectionTitle>What These Laws Require</SectionTitle>

            <TwoColumnGrid>
              <Column>
                <SectionSubtitle>All-In Pricing</SectionSubtitle>
                <List>
                  <ListItem>
                    Your advertised price must be the total cost (excluding only
                    sales tax)
                  </ListItem>
                  <ListItem>
                    No more "$25 + fees" — it becomes "$28.50 all-in"
                  </ListItem>
                  <ListItem>
                    Applies across websites, social, print, and ads
                  </ListItem>
                  <ListItem>
                    Your advance, online, and box-office prices must all align
                    with what's advertised
                  </ListItem>
                </List>
              </Column>

              <Column>
                <SectionSubtitle>Marketing & Pricing Strategy</SectionSubtitle>
                <List>
                  <ListItem>
                    You can still use tiered pricing (e.g., "Buy Early & Save"),
                    but the full price at each tier must be clear
                  </ListItem>
                  <ListItem>
                    Marketing should focus on value and timing, not "plus fees"
                    disclaimers
                  </ListItem>
                  <ListItem>
                    Checkout totals can be less than advertised (e.g., with
                    discounts) but never more
                  </ListItem>
                </List>
              </Column>
            </TwoColumnGrid>

            <Paragraph>
              <strong>Event Cancellations & Postponements:</strong> New refund
              standards are part of the federal proposal. These define how
              promoters handle cancelled and rescheduled events. Full details
              covered in a later step.
            </Paragraph>
          </AnimatedSection>
        </ContentWrapper>

        <Footer>
          <Progress>Step 1 of 5</Progress>
          <ButtonGroup>
            <Button onClick={handleSkipToCalculator}>Skip to Calculator</Button>
            <Button primary onClick={handleNext}>
              Next: Understand All-In Pricing →
            </Button>
          </ButtonGroup>
        </Footer>
      </PageContainer>
    </EnhancedThemeProvider>
  );
};

export default AllInPricingComing;
